import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  MapPin, 
  Phone, 
  Star, 
  Car, 
  Users, 
  Award, 
  Clock,
  ArrowRight,
  ChevronRight,
  Shield,
  Zap,
  Target,
  HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import axios from 'axios';
import { API } from '@/App';
import HeroSlider from '@/components/HeroSlider';
import { useOrganizationSchema, useFAQSchema, commonFAQs } from '@/utils/aiSeoSchemas';
import usePageContent from '@/hooks/usePageContent';

// Testfahrt Slider Component
const TestfahrtSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const slides = [
    {
      src: "https://static.prod-images.emergentagent.com/jobs/303cab3b-acc1-4381-b3cd-1e06f864619c/images/f63cefa5c2402a24e724c5b552fdbbd70f88bfb04d69f8dacd231f1c12fe64bc.png",
      alt: "GPS-Tracking der Prüfungsroute"
    },
    {
      src: "https://static.prod-images.emergentagent.com/jobs/303cab3b-acc1-4381-b3cd-1e06f864619c/images/4a5e1b5325ef2b59e4a637f4610028c9b3c623ff12c1c571cdcd34e73cf442fb.png",
      alt: "Prüfungsergebnis - Bestanden"
    },
    {
      src: "https://static.prod-images.emergentagent.com/jobs/303cab3b-acc1-4381-b3cd-1e06f864619c/images/6d61c97bd19ca6b6f186e7e5cfefee981c146882b42a3aecf2cc9816dce0674c.png",
      alt: "Aufgezeichnete Routen"
    },
    {
      src: "https://static.prod-images.emergentagent.com/jobs/303cab3b-acc1-4381-b3cd-1e06f864619c/images/01df819f49786d084d6cbdc8d2568ce261406e78c8fdc41ba8a9db82f6efce87.png",
      alt: "Fahrlehrer-Feedback"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="relative bg-slate-100">
      {/* Images */}
      <div className="relative overflow-hidden">
        {slides.map((slide, index) => (
          <img
            key={index}
            src={slide.src}
            alt={slide.alt}
            className={`w-full h-auto transition-opacity duration-700 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'
            }`}
          />
        ))}
      </div>
      
      {/* Dots Navigation */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-emerald-500 w-8' 
                : 'bg-white/60 hover:bg-white/80'
            }`}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const HomePage = () => {
  const { t: tr } = useTranslation();
  const { t: c } = usePageContent('homepage');
  const [locations, setLocations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [articles, setArticles] = useState([]);
  const [sliderImages, setSliderImages] = useState([]);

  // AI-optimized Schema.org structured data
  useOrganizationSchema();
  useFAQSchema(commonFAQs.general);

  useEffect(() => {
    // Optimized SEO Title & Description
    document.title = 'Fahrschule Frankfurt, Offenbach & Bad Homburg | Intensivkurs & Klasse B | Rößner';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Fahrschule Rößner in Frankfurt Schwanheim, Niederrad, Offenbach & Bad Homburg. Führerschein Klasse B, Intensivkurse in 2-4 Wochen. 34 Jahre Erfahrung, 95% Bestehensquote. Jetzt anmelden!');
    }

    axios.get(`${API}/locations`).then(res => setLocations(res.data)).catch(() => {});
    axios.get(`${API}/testimonials`).then(res => setTestimonials(res.data)).catch(() => {});
    axios.get(`${API}/blog`).then(res => setArticles(res.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/slider`).then(res => setSliderImages(res.data)).catch(() => {});
  }, []);

  const features = [
    { 
      icon: Award, 
      title: c('feature_1_title', tr('features.experience.title')), 
      desc: c('feature_1_desc', tr('features.experience.desc')),
      color: 'from-blue-500 to-cyan-500'
    },
    { icon: Users, 
      title: c('feature_2_title', tr('features.exams.title')), 
      desc: c('feature_2_desc', 'Über 95% unserer Fahrschüler bestehen die Prüfung beim ersten Anlauf.'),
      color: 'from-emerald-500 to-teal-500'
    },
    { 
      icon: Car, 
      title: c('feature_3_title', tr('features.vehicles.title')), 
      desc: c('feature_3_desc', tr('features.vehicles.desc')),
      color: 'from-violet-500 to-purple-500'
    },
    { 
      icon: Clock, 
      title: c('feature_4_title', tr('features.flexible.title')), 
      desc: c('feature_4_desc', tr('features.flexible.desc')),
      color: 'from-orange-500 to-amber-500'
    },
  ];

  const whyUs = [
    { icon: Shield, title: c('whyus_1_title', 'Sicherheit'), desc: c('whyus_1_desc', 'Höchste Sicherheitsstandards in der Ausbildung') },
    { icon: Zap, title: c('whyus_2_title', 'Effizienz'), desc: c('whyus_2_desc', 'Strukturierte Ausbildung für schnellen Erfolg') },
    { icon: Target, title: c('whyus_3_title', 'Erfolgsquote'), desc: c('whyus_3_desc', 'Überdurchschnittliche Bestehensquote') },
  ];

  return (
    <div data-testid="homepage">
      {/* Hero Slider */}
      {sliderImages.length > 0 ? (
        <HeroSlider slides={sliderImages} />
      ) : (
        <section className="relative h-screen min-h-[700px] flex items-center bg-slate-900">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1920&q=80"
              alt="Moderne Fahrschule"
              className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.1] mb-6">
                {c('hero_title', 'Dein Führerschein startet hier')}
              </h1>
              <p className="text-xl text-slate-300 mb-10">
                {c('hero_subtitle', 'Fahrschule Rößner – Dein Partner für sicheres Fahren in Frankfurt, Offenbach & Bad Homburg.')}
              </p>
              <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
                <Button type="submit" size="lg" className="h-14 px-10 text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full">
                  Jetzt anmelden <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Features Section - Modern Bento Grid */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="features-section">
        <div className="absolute inset-0 bg-grid opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              {c('features_badge', tr('features.badge'))}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              {c('features_title', tr('features.title'))}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {c('features_subtitle', tr('features.subtitle'))}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="card-modern group p-8"
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section - Modern Cards */}
      <section className="py-24 bg-slate-50 relative" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-12">
            <div>
              <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
                {tr('locations.badge')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                {tr('locations.title')}
              </h2>
              <p className="text-xl text-slate-600 max-w-xl">
                {tr('locations.subtitle')}
              </p>
            </div>
            <Link to="/kontakt">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 h-12">
                {tr('locations.showAll')} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {locations.map((location, index) => {
              // Luftaufnahmen für jeden Standort
              const mapImages = {
                'frankfurt-schwanheim': 'https://images.unsplash.com/photo-1653250545519-2fdbc994812a?w=400&h=300&fit=crop',
                'frankfurt-niederrad': 'https://images.unsplash.com/photo-1662281691238-d7e4e0b625fa?w=400&h=300&fit=crop',
                'offenbach': 'https://images.pexels.com/photos/2514002/pexels-photo-2514002.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
                'bad-homburg': 'https://images.pexels.com/photos/36696311/pexels-photo-36696311.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop'
              };
              const mapUrl = mapImages[location.slug] || 'https://images.unsplash.com/photo-1513496335913-a9aab0fc1318?w=400&h=300&fit=crop';
              
              return (
              <Link 
                key={location.slug} 
                to={`/standorte/${location.slug}`}
                className="group block"
                data-testid={`location-card-${location.slug}`}
              >
                <div className="card-modern h-full">
                  <div className="relative h-48 overflow-hidden rounded-t-2xl bg-slate-200">
                    {/* Karten-Hintergrundbild */}
                    <img 
                      src={mapUrl}
                      alt={`${location.name} Luftaufnahme`}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Dunkler Overlay für bessere Lesbarkeit */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-slate-900/30"></div>
                    {/* Standort-Name */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-2xl font-bold text-white">{location.name}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-slate-600 mb-4">{location.address}</p>
                    <div className="flex items-center text-blue-600 font-semibold group-hover:translate-x-2 transition-transform">
                      {tr('locations.viewDetails')} <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              </Link>
            )})}
          </div>
        </div>
      </section>

      {/* Why Us Section - Modern Dark */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-semibold mb-6">
                {c('whyus_badge', 'Dein Erfolg ist unser Ziel')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {c('whyus_title', 'Bestens vorbereitet für ')} <span className="text-gradient">deine Prüfung</span>
              </h2>
              <p className="text-xl text-slate-400 mb-10 leading-relaxed">
                {c('whyus_subtitle', 'Mit unserer strukturierten Ausbildung und modernen Lernmethoden bringen wir dich sicher zum Führerschein.')}
              </p>
              
              <div className="space-y-6">
                {whyUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-700/50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_303cab3b-acc1-4381-b3cd-1e06f864619c/artifacts/azbt4rgy_ChatGPT%20Image%2030.%20M%C3%A4rz%202026%2C%2013_27_38.png"
                  alt="Fahrschule Rößner Fahrzeugflotte"
                  className="w-full h-auto rounded-3xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
              </div>
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 glass-dark rounded-2xl p-6 shadow-2xl border border-slate-700/50">
                <div className="text-4xl font-black text-white mb-1">{c('whyus_stat', '95%')}</div>
                <div className="text-slate-400 text-sm">{c('whyus_stat_label', 'Bestehensquote')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prüfungstestfahrt Feature Section */}
      <section className="py-24 bg-gradient-to-br from-blue-50 via-white to-emerald-50 relative overflow-hidden" data-testid="testfahrt-section">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6">
                <Target className="w-4 h-4" />
                {c('testfahrt_badge', 'Exklusiv bei uns')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                {c('testfahrt_title', 'Prüfungs-Testfahrt')}
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {c('testfahrt_subtitle', 'Wir simulieren deine praktische Prüfung unter realen Bedingungen. Alle Fehler und Stärken werden detailliert protokolliert – damit du optimal vorbereitet in die echte Prüfung gehst.')}
              </p>
              
              {/* Feature List */}
              <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-slate-100">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{c('testfahrt_feature_1_title', 'Strecke nachfahrbar')}</h4>
                    <p className="text-slate-600 text-sm">{c('testfahrt_feature_1_desc', 'Die gefahrene Route mit Eltern oder Freunden wiederholen')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-slate-100">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{c('testfahrt_feature_2_title', 'Fehlerübersicht')}</h4>
                    <p className="text-slate-600 text-sm">{c('testfahrt_feature_2_desc', 'Alle Fehlerpunkte werden protokolliert und auf der Karte markiert')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-slate-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{c('testfahrt_feature_3_title', 'Fahrlehrer-Bemerkungen')}</h4>
                    <p className="text-slate-600 text-sm">{c('testfahrt_feature_3_desc', 'Persönliche Tipps und Feedback nach jeder Testfahrt')}</p>
                  </div>
                </div>
              </div>
              
              <Link to="/kontakt">
                <Button size="lg" className="btn-primary text-lg px-8 py-6">
                  Jetzt anmelden
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            
            {/* Right: Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                {/* Professional App Screenshot */}
                <div className="relative bg-slate-100">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/303cab3b-acc1-4381-b3cd-1e06f864619c/images/f63cefa5c2402a24e724c5b552fdbbd70f88bfb04d69f8dacd231f1c12fe64bc.png"
                    alt="Prüfungstestfahrt App - Route mit Fehlermarkierungen"
                    className="w-full h-auto"
                  />
                </div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-4 -right-4 bg-emerald-500 text-white rounded-2xl px-6 py-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <div className="font-bold text-lg">{c('testfahrt_badge_text', 'Bestanden?')}</div>
                    <div className="text-emerald-100 text-sm">{c('testfahrt_badge_sub', 'Sieh es vorher!')}</div>
                  </div>
                </div>
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -top-4 -left-4 bg-white rounded-2xl px-5 py-4 shadow-lg border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{c('testfahrt_gps_title', 'GPS-Tracking')}</div>
                    <div className="text-slate-500 text-sm">{c('testfahrt_gps_sub', 'Live-Aufzeichnung')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="testimonials-section">
        <div className="absolute inset-0 bg-grid opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-full mb-4">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-yellow-700 text-sm font-semibold">5.0 von 5 Sternen</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              {c('testimonials_title', 'Das sagen unsere Fahrschüler')}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {c('testimonials_subtitle', 'Echte Erfahrungen von echten Fahrschülern.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 6).map((testimonial, index) => (
              <div 
                key={testimonial.id || index} 
                className="card-modern p-8"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 mb-8 leading-relaxed text-lg">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-4 pt-6 border-t border-slate-100">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {testimonial.name?.charAt(0) || 'F'}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-lg">{testimonial.name}</div>
                    <div className="text-slate-500">{testimonial.location} • Klasse {testimonial.license_type}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="py-24 bg-slate-50" data-testid="blog-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
            <div>
              <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
                {c('blog_badge', 'Ratgeber')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
                {c('blog_title', 'Tipps & Wissen')}
              </h2>
            </div>
            <Link to="/blog">
              <Button variant="outline" className="rounded-full px-8 h-12 border-2 hover:bg-slate-900 hover:text-white hover:border-slate-900" data-testid="blog-view-all">
                Alle Artikel <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Link 
                key={article.slug} 
                to={`/blog/${article.slug}`}
                className="group block"
                data-testid={`blog-card-${index}`}
              >
                <div className="card-modern overflow-hidden h-full">
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={article.image_url} 
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 line-clamp-2 mb-4">{article.excerpt}</p>
                    <span className="text-blue-600 font-semibold inline-flex items-center group-hover:translate-x-2 transition-transform">
                      Weiterlesen <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - AI Optimized */}
      <section className="py-24 bg-white" id="faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
              <HelpCircle className="w-4 h-4 mr-2" />
              Häufige Fragen
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              {c('faq_title', 'Fragen zum Führerschein?')}
            </h2>
            <p className="text-lg text-slate-600">
              {c('faq_subtitle', 'Hier findest du Antworten auf die häufigsten Fragen rund um die Führerscheinausbildung.')}
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {[1,2,3,4,5,6].map((i) => {
              const question = c(`faq_${i}_question`, commonFAQs.general[i-1]?.question);
              const answer = c(`faq_${i}_answer`, commonFAQs.general[i-1]?.answer);
              if (!question) return null;
              return (
                <AccordionItem 
                  key={i} 
                  value={`faq-${i}`} 
                  className="border border-slate-200 rounded-xl px-6 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                >
                  <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-blue-600 py-5">
                    {question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                    {answer}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
          
          <div className="text-center mt-12">
            <p className="text-slate-600 mb-4">{c('faq_contact_text', 'Noch mehr Fragen? Wir helfen dir gerne!')}</p>
            <Link to="/kontakt">
              <Button className="bg-blue-600 hover:bg-blue-700 rounded-full px-8">
                Kontakt aufnehmen
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/30 rounded-full blur-[120px]"></div>
        
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="inline-block px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-semibold mb-8">
            {c('cta_badge', 'Starte jetzt')}
          </span>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
            {c('cta_title', 'Bereit für deinen Führerschein?')}
          </h2>
          <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
            {c('cta_subtitle', 'Vereinbare jetzt dein kostenloses Beratungsgespräch und starte in die Freiheit!')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button 
                size="lg" 
                className="h-16 px-12 text-lg font-bold bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white rounded-full shadow-2xl shadow-blue-500/30 btn-glow"
                data-testid="cta-primary"
              >
                {c('cta_button_primary', 'Kostenlose Beratung')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+496920326059">
              <Button 
                size="lg" 
                variant="outline" 
                className="h-16 px-12 text-lg font-semibold border-2 border-white/20 text-white hover:bg-white/10 rounded-full"
                data-testid="cta-phone"
              >
                <Phone className="mr-2 w-5 h-5" />
                069 20 32 60 59
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
